import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, Routes, Route, Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import LeagueStandings from '../LeagueStandings';
import TeamTransfersDropdown from '../Transfers/TeamTransfersDropdown';
import ManagerSummary from '../ManagerSummary';
import LandingPage from '../LandingPage';
import { NotificationIcon } from '../Notification/NotificationIcon';
import ThemeToggle from '../UI/ThemeToggle';
import { useTeamContext } from '../../contexts/TeamContext';
import { Pencil } from 'lucide-react';
import PointsHit from '../Transfers/PointsHit';
import ChipsUsed from '../Chips/ChipsUsed';
import LiveDefensiveContributions from '../DefensiveContributions/LiveDefensiveContributions';
import { useIsDefConLive } from '../../hooks/useIsDefConLive';

const NAV_ITEMS = [
  { path: '/standings', label: 'League Standings' },
  { path: '/transfers', label: 'Team Transfers' },
  { path: '/chips', label: 'Chips Used' },
  { path: '/defensive', label: 'Def-Con' },
  { path: '/manager', label: 'Manager Summary' },
];

// min-h-11 == 44px tap target, applied to every nav control.
const NAV_LINK =
  'inline-flex min-h-11 items-center px-2 rounded-md hover:text-accent transition-colors';

const NavMenu: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { leagueId } = useTeamContext();
  const isDefConLive = useIsDefConLive();

  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 1024);
      if (window.innerWidth >= 1024) {
        setIsOpen(false);
      }
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);

    return () => {
      window.removeEventListener('resize', checkScreenSize);
    };
  }, []);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const handleNavClick = (path: string) => {
    navigate(path);
    if (isMobile) {
      setIsOpen(false);
    }
  };

  const LiveBadge = ({ className = '' }: { className?: string }) => (
    <span className={`absolute flex items-center gap-1 ${className}`}>
      <span className="text-2xs font-semibold leading-none text-green-600 dark:text-green-400">Live</span>
      <span className="h-1.5 w-1.5 rounded-full bg-green-600 dark:bg-green-400 animate-pulse" />
    </span>
  );

  return (
    <div>
      <header className={`bg-surface text-content shadow-md z-10 ${!isOpen ? 'sticky top-0' : 'relative'}`}>
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <Link to="/standings" className="inline-flex min-h-11 items-center text-xl font-bold">
                FPL Standings
              </Link>
            </div>

            {/* Mobile controls */}
            <div className="flex items-center gap-1 lg:hidden">
              <ThemeToggle />
              <button
                onClick={toggleMenu}
                className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-md hover:bg-content/10 transition-colors focus:outline-none"
                aria-expanded={isOpen}
                aria-label={isOpen ? 'Close menu' : 'Open menu'}
              >
                <svg
                  className="h-6 w-6"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  {isOpen ? (
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  ) : (
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 6h16M4 12h16M4 18h16"
                    />
                  )}
                </svg>
              </button>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden lg:block">
              <ul className="flex items-center gap-1">
                {NAV_ITEMS.map((item) => (
                  <li key={item.path}>
                    <button
                      onClick={() => handleNavClick(item.path)}
                      className={`relative ${NAV_LINK} ${
                        location.pathname === item.path ? 'font-bold underline' : ''
                      }`}
                    >
                      {item.label}
                      {item.path === '/defensive' && isDefConLive && <LiveBadge className="-top-1 right-0" />}
                    </button>
                  </li>
                ))}
                {leagueId && location.pathname !== '/' && (
                  <li>
                    <button
                      onClick={() => handleNavClick('/')}
                      aria-label={`Change league (currently ${leagueId})`}
                      className={`gap-1 text-content-secondary ${NAV_LINK}`}
                    >
                      {leagueId} <Pencil size={18} />
                    </button>
                  </li>
                )}
                <li>
                  <NotificationIcon />
                </li>
                <li>
                  <ThemeToggle />
                </li>
              </ul>
            </nav>
          </div>
        </div>

        {/* Mobile Navigation Menu */}
        <div
          className={`fixed top-16 left-0 w-full h-[calc(100dvh-4rem)] lg:hidden bg-surface text-content transition-transform duration-300 ease-in-out transform z-20 ${
            isOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
        >
          <nav className="h-full overflow-y-auto">
            <ul className="flex flex-col items-center gap-4 px-4 pt-8 pb-8 text-xl">
              {NAV_ITEMS.map((item) => (
                <li key={item.path}>
                  <button
                    onClick={() => handleNavClick(item.path)}
                    className={`relative ${NAV_LINK} ${
                      location.pathname === item.path ? 'font-bold underline' : ''
                    }`}
                  >
                    {item.label}
                    {item.path === '/defensive' && isDefConLive && <LiveBadge className="-top-1 -right-10" />}
                  </button>
                </li>
              ))}
              <li>
                <NotificationIcon customSize={40} />
              </li>
              {leagueId && location.pathname !== '/' && (
                <li>
                  <button
                    onClick={() => handleNavClick('/')}
                    aria-label={`Change league (currently ${leagueId})`}
                    className={`flex-col gap-1 text-content-secondary ${NAV_LINK}`}
                  >
                    FPL ID: {leagueId} <Pencil size={18} />
                  </button>
                </li>
              )}
            </ul>
          </nav>
        </div>

        {isOpen && (
          <div
            className="fixed inset-0 bg-black/25 lg:hidden z-0"
            onClick={() => setIsOpen(false)}
          ></div>
        )}
      </header>

      {/* Animated Content Section */}
      <main
        className={`container mx-auto mt-8 min-h-[70dvh] overflow-hidden pb-[calc(env(safe-area-inset-bottom)+1rem)] ${isOpen && "fixed"}`}
      >
        <AnimatePresence mode="wait">
          <motion.div
            key={location.pathname}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.4, ease: 'easeInOut' }}
          >
            <Routes location={location} key={location.pathname}>
              <Route path="/" element={<LandingPage />} />
              <Route path="/standings" element={<LeagueStandings />} />
              <Route path="/transfers" element={<TeamTransfersDropdown />} />
              <Route path="/manager" element={<ManagerSummary />} />
              <Route path="/pointsHit" element={<PointsHit />} />
              <Route path="/chips" element={<ChipsUsed />} />
              <Route path="/defensive" element={<LiveDefensiveContributions />} />
            </Routes>
          </motion.div>
        </AnimatePresence>
      </main>
    </div>
  );
};

export default NavMenu;
